import { useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Search, X } from 'lucide-react';

import { PROJECT_PRIORITIES, PROJECT_PRIORITY_LABELS } from '../api/types';
import { statusOptions } from '../lib/statuses';
import { DatePicker } from './DatePicker';
import { MultiSelect } from './MultiSelect';
import { useRegistryFilterContext, useFilters } from '../state/FiltersProvider';

interface FilterBarProps {
  /** View-local filters (team, category, sort…) appended after the global ones. */
  extra?: ReactNode;
  searchPlaceholder?: string;
}

/**
 * F-13/F-14: the global filters (status, stream, person, team, at-risk, search)
 * live on EVERY view. View-specific controls arrive through `extra`. All state
 * is in the URL — this component only reads and writes it.
 *
 * The five dictionary dimensions (status, stream, person, team, priority) are
 * MULTI-selects (owner request): each accepts several values, the predicate
 * OR-s them within a key. Category / at-risk / search stay single-valued.
 * Status options come from the MANAGED dictionary (`registry.statuses`, F-23)
 * — dictionary order, current labels, custom statuses included.
 */
export function FilterBar({ extra, searchPlaceholder = 'Поиск по названию, описанию, исполнителю…' }: FilterBarProps): ReactElement {
  const { filters, setFilters, clear, isActive } = useFilters();
  const { statuses, people, streams, teams } = useRegistryFilterContext();
  const [search, setSearch] = useState(filters.search ?? '');
  const timer = useRef<number | undefined>(undefined);

  // A filter set from elsewhere (a chip, a link) updates the box.
  useEffect(() => {
    setSearch(filters.search ?? '');
  }, [filters.search]);

  // Debounced commit: the URL must not be rewritten per keystroke.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const commitSearch = (value: string) => setFilters({ ...filters, search: value.trim() || undefined });

  return (
    <div className="toolbar" role="search" aria-label="Фильтры реестра" data-testid="filter-bar">
      <MultiSelect
        id="filter-status"
        label="Статус"
        placeholder="Все статусы"
        testId="filter-status"
        options={statusOptions(statuses)}
        selected={filters.status ?? []}
        onChange={(values) => setFilters({ ...filters, status: values.length > 0 ? (values as typeof filters.status) : undefined })}
      />

      <MultiSelect
        id="filter-stream"
        label="Поток"
        placeholder="Все потоки"
        testId="filter-stream"
        options={streams.map((stream) => ({ value: stream.id, label: stream.name }))}
        selected={filters.streamId ?? []}
        onChange={(values) => setFilters({ ...filters, streamId: values.length > 0 ? values : undefined })}
      />

      <MultiSelect
        id="filter-person"
        label="Исполнитель"
        placeholder="Все исполнители"
        testId="filter-person"
        options={people.map((person) => ({ value: person.id, label: person.active ? person.name : `${person.name} (неактивен)` }))}
        selected={filters.personId ?? []}
        onChange={(values) => setFilters({ ...filters, personId: values.length > 0 ? values : undefined })}
      />

      <MultiSelect
        id="filter-team"
        label="Команда"
        placeholder="Все команды"
        testId="filter-team"
        options={teams.map((team) => ({ value: team.id, label: team.name }))}
        selected={filters.teamId ?? []}
        onChange={(values) => setFilters({ ...filters, teamId: values.length > 0 ? values : undefined })}
      />

      <MultiSelect
        id="filter-priority"
        label="Приоритет"
        placeholder="Любой"
        testId="filter-priority"
        options={PROJECT_PRIORITIES.map((priority) => ({ value: priority, label: PROJECT_PRIORITY_LABELS[priority] }))}
        selected={filters.priority ?? []}
        onChange={(values) => setFilters({ ...filters, priority: values.length > 0 ? (values as typeof filters.priority) : undefined })}
      />

      {/* Period on «Срок до» (owner request): the same dd.mm.yyyy control as
          the row/modal dates; unset = no bound, one bound = one-sided filter. */}
      <div className="toolbar__field">
        <label className="toolbar__label" htmlFor="filter-due-from">
          Срок от
        </label>
        <DatePicker
          id="filter-due-from"
          label="Срок от (включительно)"
          testId="filter-due-from"
          className="input--compact"
          value={filters.dueFrom ?? null}
          onChange={(iso) => setFilters({ ...filters, dueFrom: iso ?? undefined })}
        />
      </div>

      <div className="toolbar__field">
        <label className="toolbar__label" htmlFor="filter-due-to">
          Срок до
        </label>
        <DatePicker
          id="filter-due-to"
          label="Срок до (включительно)"
          testId="filter-due-to"
          className="input--compact"
          value={filters.dueTo ?? null}
          onChange={(iso) => setFilters({ ...filters, dueTo: iso ?? undefined })}
        />
      </div>

      {/* «Только со сроком» (owner request 2026-10-23, F-28): one click hides
          the dateless projects — the same dueDate dimension (F-04) as the
          period bounds next to it, just without a value. */}
      <label className="check-field" htmlFor="filter-has-due">
        <input
          id="filter-has-due"
          type="checkbox"
          className="checkbox"
          checked={filters.hasDueDate === true}
          onChange={(event) => setFilters({ ...filters, hasDueDate: event.target.checked || undefined })}
        />
        Только со сроком
      </label>

      <label className="check-field" htmlFor="filter-risk">
        <input
          id="filter-risk"
          type="checkbox"
          className="checkbox"
          checked={filters.atRisk === true}
          onChange={(event) => setFilters({ ...filters, atRisk: event.target.checked || undefined })}
        />
        Только at-risk
      </label>

      <div className="toolbar__field toolbar__field--grow">
        <label className="toolbar__label" htmlFor="filter-search">
          Поиск
        </label>
        <div className="search-box">
          <Search size={15} aria-hidden="true" className="search-box__icon" />
          <input
            id="filter-search"
            type="search"
            className="input input--compact search-box__input"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              window.clearTimeout(timer.current);
              timer.current = window.setTimeout(() => commitSearch(event.target.value), 350);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                window.clearTimeout(timer.current);
                commitSearch(search);
              }
            }}
            onBlur={() => {
              window.clearTimeout(timer.current);
              if ((filters.search ?? '') !== search.trim()) commitSearch(search);
            }}
          />
        </div>
      </div>

      {extra}

      {isActive ? (
        <button type="button" className="btn btn--ghost btn--sm" onClick={clear}>
          <X size={14} aria-hidden="true" /> Сбросить фильтры
        </button>
      ) : null}
    </div>
  );
}
